import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { requestLoginCode, verifyLoginCode, updateProfile } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ExpressIdentityFields } from './ExpressIdentityFields';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft, Loader2, PartyPopper } from 'lucide-react';
import GoogleAuthButton from './GoogleAuthButton';

const emptyForm = { email: '', first_name: '', last_name: '', phone: '', country: '', postcode: '', address: '', city: '' };

// "Rejoindre": email -> 6-digit code -> account created/signed in -> optional profile completion.
export default function JoinModal({ open, onOpenChange, onSuccess }) {
  const { login } = useAuth();
  const { t } = useLang();
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep('email'); setEmail(''); setCode(''); setForm(emptyForm); };

  const handleOpenChange = (v) => { if (!v) reset(); onOpenChange(v); };

  const sendCode = async () => {
    if (!email.trim()) { toast.error(t('expEmail')); return; }
    setBusy(true);
    try {
      await requestLoginCode({ email: email.trim(), create: true });
      setStep('code');
      toast.success(t('expCodeSent'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.trim().length !== 6) { toast.error(t('expEnterCode')); return; }
    setBusy(true);
    try {
      const res = await verifyLoginCode({ email: email.trim(), code: code.trim() });
      login(res.data.token, res.data.user);
      const u = res.data.user || {};
      setForm({
        email: u.email || email.trim(), first_name: u.first_name || '', last_name: u.last_name || '',
        phone: u.phone || '', country: u.country || '', postcode: u.postcode || '',
        address: u.address || '', city: u.city || '',
      });
      setStep('profile');
    } catch (e) { toast.error(e.response?.data?.detail || 'Code incorrect'); }
    finally { setBusy(false); }
  };

  const saveProfile = async () => {
    setBusy(true);
    try {
      const { email: _e, ...profile } = form;
      await updateProfile(profile);
      toast.success(t('profileSaved'));
      finish();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  const finish = () => { onSuccess?.(); handleOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white border-border sm:max-w-md p-6" data-testid="join-modal">
        <DialogHeader className="sr-only"><DialogTitle>Rejoindre</DialogTitle><DialogDescription>Inscription sans mot de passe</DialogDescription></DialogHeader>

        {step === 'email' && (
          <div className="space-y-4" data-testid="join-step-email">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mx-auto mb-3"><Sparkles className="w-6 h-6 text-gold-dark" /></div>
              <h2 className="font-display text-xl font-bold text-rolex-green">{t('joinTitle')}</h2>
              <p className="text-sm text-rolex-green/65 mt-1">{t('joinDesc')}</p>
            </div>
            <GoogleAuthButton />
            <div className="relative text-center">
              <span className="bg-white px-3 text-[11px] uppercase tracking-widest text-rolex-green/45 relative z-10">{t('orDivider')}</span>
              <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-rolex-green font-semibold text-sm">{t('expEmail')}</Label>
              <Input type="email" autoComplete="email" placeholder="votre@email.com" value={email}
                onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendCode()}
                className="rounded-xl h-12" data-testid="join-email-input" />
            </div>
            <Button onClick={sendCode} disabled={busy} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold" data-testid="join-send-code">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('expSendCode')}
            </Button>
          </div>
        )}

        {step === 'code' && (
          <div className="space-y-4" data-testid="join-step-code">
            <button type="button" onClick={() => setStep('email')} className="flex items-center gap-1 text-sm text-rolex-green/65 hover:text-rolex-green">
              <ArrowLeft className="w-3.5 h-3.5" /> {email}
            </button>
            <div className="text-center">
              <h2 className="font-display text-xl font-bold text-rolex-green">{t('expEnterCode')}</h2>
              <p className="text-sm text-rolex-green/65 mt-1">{t('expSignInDesc')}</p>
            </div>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && verify()} inputMode="numeric" maxLength={6} placeholder="••••••"
              className="rounded-xl text-center font-mono text-2xl tracking-[0.4em] h-14" data-testid="join-code-input" />
            <Button onClick={verify} disabled={busy} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold" data-testid="join-verify-code">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('expVerify')}
            </Button>
            <button type="button" onClick={sendCode} className="w-full text-xs text-rolex-green/55 hover:text-rolex-green underline-offset-2 hover:underline">{t('expResendCode')}</button>
          </div>
        )}

        {step === 'profile' && (
          <div className="space-y-3 max-h-[80vh] overflow-y-auto" data-testid="join-step-profile">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center mx-auto mb-2"><PartyPopper className="w-6 h-6 text-emerald-600" /></div>
              <h2 className="font-display text-xl font-bold text-rolex-green">{t('updateProfileTitle')}</h2>
              <p className="text-sm text-rolex-green/65 mt-1">{t('updateProfileDesc')}</p>
            </div>
            <ExpressIdentityFields form={form} setForm={setForm} />
            <Button onClick={saveProfile} disabled={busy} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-bold" data-testid="join-save-profile">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('saveProfile')}
            </Button>
            <button type="button" onClick={finish} className="w-full text-sm text-rolex-green/55 hover:text-rolex-green py-1" data-testid="join-skip-profile">{t('skipForNow')}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
